import jsonData from '@data/attack-tools.json';
import * as React from 'react';
import styled from 'styled-components';
import {
  VictoryArea,
  VictoryAxis,
  VictoryChart,
  VictoryContainer,
  VictoryLegend,
  VictoryStack,
  VictoryTooltip,
  VictoryVoronoiContainer,
} from 'victory';

const colorScale = Array.from({ length: 9 }).map((_, i) => `var(--color-scale-${i + 1}0)`);

const data = jsonData.entries.map(({ name, data }, toolIndex) => ({
  name,
  data: data.map((d) => ({
    x: new Date(d.date),
    y: d.percentage,
    toolName: name,
  })),
  symbol: {
    fill: colorScale[toolIndex % colorScale.length],
  },
}));

const Container = styled.div`
  .VictoryContainer > svg {
    overflow: visible;
  }
`;

const formatDate = (d: Date): string => `${d.getUTCMonth() + 1}/${d.getUTCDate()}/${d.getUTCFullYear()}`;
const animationDuration = 2000;

type Props = {
  height?: number;
};

const AttackToolsChart = ({ height = 400 }: Props): JSX.Element | null => {
  // the default onLoad transition draws the data out horizontally so to get the vertical animation
  // we transition the data itself and use the onEnter animation to animate the data in
  const [transitionData, setTransitionData] = React.useState(
    data.map((d) => ({ ...d, data: d.data.map((t) => ({ ...t, y: 0 })) }))
  );
  // used disable animation on the chart after the initial animation. otherwise tooltips get animated
  const [hasAnimated, setHasAnimated] = React.useState(false);

  React.useEffect(() => {
    setTimeout(() => setTransitionData(data), 0);
    setTimeout(() => setHasAnimated(true), animationDuration);
  }, []);

  const startDate = new Date(jsonData.startDate);
  const endDate = new Date(jsonData.endDate);
  const middleDate = new Date(startDate);
  middleDate.setUTCDate(middleDate.getUTCDate() + 7);

  return (
    <Container>
      <VictoryChart
        scale={{ x: 'time' }}
        domain={{ x: [startDate, endDate], y: [0, 105] }}
        padding={{ top: 10, bottom: 100, left: 30, right: 20 }}
        height={height}
        containerComponent={
          hasAnimated ? (
            <VictoryVoronoiContainer
              labelComponent={<VictoryTooltip active />}
              labels={({ datum }) => (datum.y ? [datum.toolName, formatDate(datum.x), `${datum.y}%`].join('\n') : '')}
            />
          ) : (
            <VictoryContainer />
          )
        }
      >
        <VictoryStack
          colorScale={colorScale}
          height={height}
          {...(hasAnimated
            ? {}
            : { animate: { onEnter: { duration: 1 }, onLoad: { duration: 0 }, duration: animationDuration } })}
        >
          {transitionData.map((d) => {
            return <VictoryArea key={d.name} data={d.data} interpolation="monotoneX" />;
          })}
        </VictoryStack>
        <VictoryAxis
          dependentAxis
          tickValues={[0, 25, 50, 75, 100]}
          tickFormat={(d) => `${d}%`}
          style={{ tickLabels: { fontFamily: 'var(--font)' } }}
        />
        <VictoryAxis
          tickValues={[startDate, middleDate, endDate]}
          tickFormat={formatDate}
          style={{ tickLabels: { fontFamily: 'var(--font)' } }}
        />
        <VictoryLegend
          data={[...transitionData].reverse().slice(0, 8)}
          orientation="horizontal"
          x={20}
          y={height - 60}
          itemsPerRow={4}
          gutter={20}
          style={{
            data: { stroke: 'var(--color-grey-dark)', strokeWidth: 0.5 },
            labels: { fontSize: 14, fontFamily: 'var(--font)' },
          }}
        />
      </VictoryChart>
    </Container>
  );
};

export default AttackToolsChart;
